import CloseIcon from '@mui/icons-material/Close';
import {Box} from '@mui/material';
import {t} from 'i18next';
import React, {forwardRef, useCallback, useEffect, useRef} from 'react';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {QBDropTypes} from '../../../utils/queryBuilder';
import AurButton from '../../common/aur-button/AurButton';
import AurDraggableItem from '../../common/aur-draggable/AurDraggableItem';
import QueryBuilderComponent, {
  QueryBuilderComponentPropsRef,
} from './QueryBuilderComponent';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../../hooks/permissions/usePermissionsByPage';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
} from '../../../constants/analyticsConstants';
import {countRules} from './query-field/query-field-utils';
interface QueryBuilderWrapperProps {
  setQueryBuilderMode: () => void;
  onClickRunSearch: () => void;
  onPressAddRelationshipGroup: () => void;
  selectedRelationshipGroupArray?: string[];
  onSaveSearchClick?: () => void;
  saveSearchLoader?: boolean;
  onHeightChange?: () => void;
}

const QueryBuilderWrapper = forwardRef<HTMLElement, QueryBuilderWrapperProps>(
  (
    {
      setQueryBuilderMode,
      onClickRunSearch,
      onPressAddRelationshipGroup,
      selectedRelationshipGroupArray,
      onSaveSearchClick,
      saveSearchLoader,
      onHeightChange,
    },
    ref
  ) => {
    const queryBuilderRef = useRef<QueryBuilderComponentPropsRef>(null);
    const {builder} = useSelector((state: RootState) => state.query);
    const queryFieldRef = useRef<HTMLInputElement>(null);

    const {hasSavedSearchesPerm} = usePermissionsByPage(
      PAGE_NAME.INDEX_SEARCH_TAB
    );

    const onPressEnterFromDragIcon = useCallback((type: string) => {
      queryBuilderRef.current?.openOnDropItem(type);
    }, []);
    const {trackGAEvent} = useAnalytics();

    // Notify parent of height changes
    useEffect(() => {
      if (!queryFieldRef.current || !onHeightChange) return;
      const resizeObserver = new ResizeObserver(() => {
        onHeightChange();
      });
      resizeObserver.observe(queryFieldRef.current);
      return () => {
        resizeObserver.disconnect();
      };
    }, [onHeightChange]);

    const onClickSaveSearchParameters = () => {
      onSaveSearchClick?.();

      // track on save saerch button
      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name: t('gaTracking.queryBuilder.savedSearchName', {
            buttonPlace: 'top',
          }),
          region: GA_REGIONS.BODY,
          type: GA_CLICK_TYPES.BUTTON,
          action: t('gaTracking.queryBuilder.openedSearch'),
          details: t('gaTracking.queryBuilder.numberOfComponents', {
            length: countRules(builder),
          }),
        },
      });
    };

    return (
      <Box className="aur-query-builder" ref={ref}>
        <Box className="qb-header">
          <AurButton
            dataTestId="exitQueryWizardButton"
            type="exit"
            size="Medium"
            onClick={setQueryBuilderMode}
          >
            <CloseIcon fontSize="small" sx={{color: '#89A6FF'}} />
            {t('queryBuilder.exitQueryWizard')}
          </AurButton>
          <Box>
            <AurButton
              dataTestId="runSearchButton"
              type="continue"
              size="Medium"
              onClick={onClickRunSearch}
              disabled={!builder.rules.length}
              label={t('queryBuilder.runSearch')}
            />
            {hasSavedSearchesPerm && (
              <AurButton
                dataTestId="saveSearchButton"
                type="continue"
                size="Medium"
                onClick={onClickSaveSearchParameters}
                disabled={!builder.rules.length || saveSearchLoader}
                label={t('queryBuilder.saveSearchParam')}
                showLoader={saveSearchLoader}
                showLabelWithLoader={true}
              />
            )}
          </Box>
        </Box>
        <Box ref={queryFieldRef}>
          <Box className="query-builder-wrapper">
            <Box className="draggable-area">
              <AurDraggableItem
                field={{
                  name: 'name',
                  label: t('queryBuilder.defaultFields'),
                }}
                type={QBDropTypes.DEFAULT_FIELD}
                isDraggable={true}
                onPressEnterFromDragIcon={onPressEnterFromDragIcon}
              />
              <AurDraggableItem
                field={{
                  name: 'name',
                  label: t('queryBuilder.standardMetadataField'),
                }}
                type={QBDropTypes.STANDARED_METADATA_FIELD}
                isDraggable={true}
                onPressEnterFromDragIcon={onPressEnterFromDragIcon}
              />
              <AurDraggableItem
                field={{
                  name: 'email',
                  label: t('queryBuilder.customMetadataField'),
                }}
                type={QBDropTypes.CUSTOM_METADATA_FIELD}
                isDraggable={true}
                onPressEnterFromDragIcon={onPressEnterFromDragIcon}
              />
              {hasSavedSearchesPerm && (
                <AurDraggableItem
                  field={{name: 'email', label: t('queryBuilder.savedSearch')}}
                  type={QBDropTypes.SAVED_SEARCH}
                  isDraggable={true}
                  onPressEnterFromDragIcon={onPressEnterFromDragIcon}
                />
              )}
              <AurDraggableItem
                field={{name: 'email', label: t('queryBuilder.tags')}}
                type={QBDropTypes.TAG}
                isDraggable={true}
                onPressEnterFromDragIcon={onPressEnterFromDragIcon}
              />
              <AurDraggableItem
                field={{name: 'email', label: t('queryBuilder.exports')}}
                type={QBDropTypes.EXPORT}
                isDraggable={true}
                onPressEnterFromDragIcon={onPressEnterFromDragIcon}
              />
            </Box>

            <Box className="droppable-area">
              <QueryBuilderComponent
                ref={queryBuilderRef}
                selectedRelationshipGroupArray={selectedRelationshipGroupArray}
                onPressAddRelationshipGroup={onPressAddRelationshipGroup}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }
);

export default QueryBuilderWrapper;
